import {toast} from 'react-toastify'
import {
  format,
  fromUnixTime,
  parseISO,
  differenceInDays,
  differenceInMinutes,
  differenceInHours,
  parse,
  addDays,
  isBefore,
} from 'date-fns'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import brandingConfig from '../../../../config/brandingConfig'

export const copyToClipboard = (
  textToCopy: string,
  alertToast: boolean = false,
  toastMessage: string = ''
) => {
  navigator.clipboard.writeText(textToCopy)
  if (alertToast && toastMessage) {
    toast.success(toastMessage)
  }
}

export const serialize = function (obj: any) {
  let str = []
  for (let p in obj)
    if (obj.hasOwnProperty(p) && !!obj[p]) {
      str.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]))
    }
  return str.join('&')
}

export function parseJsonString(jsonString: string) {
  try {
    return JSON.parse(jsonString)
  } catch (error) {
    console.error('Error parsing JSON:', jsonString, error)
    return null // null indicates parsing error
  }
}

export const getTodayDate = () => {
  const today = new Date()
  const yyyy = today.getFullYear()
  const mm = String(today.getMonth() + 1).padStart(2, '0') // January is 0!
  const dd = String(today.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

export const formatDate = (dateInput = new Date()) => {
  const date = new Date(dateInput)
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0') // January is 0!
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

/*
  Converts a string from various programming naming conventions such as camelCase, PascalCase, kebab-case, and
  snake_case to a more readable Title Case format. This function is particularly useful for formatting
  internal string representations into a format suitable for user interfaces or documentation.
*/
export function toTitleCase(input: string) {
  // First, handle camelCase and PascalCase by adding a space before each uppercase letter (if it's not at the start).
  if (input) {
    const withSpaces = input.replace(/([A-Z])/g, (match, p1, offset) =>
      offset > 0 ? ' ' + p1 : p1
    )

    // Next, replace any underscores or hyphens with spaces, making sure to handle multiple separators together.
    const noUnderscoresOrHyphens = withSpaces.replace(/[_-]+/g, ' ')

    // Finally, capitalize the first letter of each word and make the rest lowercase.
    const titleCased = noUnderscoresOrHyphens
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')

    return titleCased
  }
}

/**
 * Sanitizes a string to be safe for use as a filename in AWS S3 by replacing spaces with dashes, collapsing consecutive spaces,
 * and removing or replacing other unsafe characters.
 *
 * EXAMPLE:  "user's   profile  picture@2024.png" => user_s-profile-picture_2024.png
 *
 * @param input The original string to be sanitized.
 * @returns A string safe for use as a file name, where only alphanumeric characters, dashes, underscores, and periods are allowed,
 * and spaces are replaced with a single dash.
 */
export function sanitizeFileName(input: string): string {
  // First, replace consecutive spaces with a single space to ensure collapsing into a single dash later
  let normalizedSpaces = input.replace(/\s+/g, ' ')
  // Replace single space with a dash
  normalizedSpaces = normalizedSpaces.replace(/ /g, '-')
  // Allow only alphanumeric characters, dashes, underscores, and periods; replace all other characters with an underscore
  const safeFileName = normalizedSpaces.replace(/[^a-zA-Z0-9-_.]/g, '_')
  return safeFileName
}

/**
 * Returns a human-readable billing period string based on numeric values and their corresponding units.
 *
 * @param billingPeriod - The numeric duration of the period (1, 2, etc.)
 * @param billingPeriodUnit - The unit of time for the billing period ("month", "day", "week", etc.)
 * @returns {string} A human-readable string describing the billing period.
 */
export function formatBillingPeriod(billingPeriod: number, billingPeriodUnit: string): string {
  switch (billingPeriodUnit?.toLowerCase()) {
    case 'day':
      if (billingPeriod === 1) {
        return 'Daily'
      } else {
        return `${billingPeriod} Days`
      }
    case 'week':
      if (billingPeriod === 1) {
        return 'Weekly'
      } else {
        return `${billingPeriod} Weeks`
      }
    case 'month':
      if (billingPeriod === 1) {
        return 'Monthly'
      } else if (billingPeriod === 3) {
        return 'Quarterly'
      } else if (billingPeriod === 6) {
        return 'Half Yearly'
      } else {
        return `${billingPeriod} Months`
      }
    case 'year':
      if (billingPeriod === 1) {
        return 'Yearly'
      } else {
        return `${billingPeriod} Years`
      }
    default:
      return 'Unknown Billing Period'
  }
}

/**
 * Formats a date given either as a Unix timestamp in seconds or as an ISO standard date string.
 * If parsing fails, the input string is returned as is. The output format can be specified,
 * defaulting to "MMM dd, yyyy" if not provided.
 *
 * @param {number|string} dateInput - The date input which can be a Unix timestamp in seconds or an ISO date string.
 * @param {string} [outputFormat="dd MMM, yyyy"] - Optional. The format string for the output date.
 * @returns {string} The formatted date string in the specified format, or the original input if parsing fails.
 */
export const getFormattedDate = (dateInput: number | string, outputFormat = brandingConfig.defaultDateFormat) => {
  let date

  if (typeof dateInput === 'number') {
    // Assume it's a Unix timestamp in seconds
    date = fromUnixTime(dateInput)
  } else if (typeof dateInput === 'string') {
    try {
      // Attempt to parse the string assuming it's in ISO format
      date = parseISO(dateInput)
      if (isNaN(date.getTime())) {
        // Check if date is valid
        return dateInput // Return original input if parsing fails
      }
    } catch (e) {
      return dateInput // Return original input if an error occurs
    }
  } else {
    return 'Invalid input' // Return error message if input type is incorrect
  }

  return format(date, outputFormat)
}

/**
 * Calculates the number of full days between a given Unix timestamp and the current time.
 *
 * @param {number} unixTimestamp - The Unix timestamp in seconds.
 * @returns {number} The number of full days between the given timestamp and now.
 */
export const getDaysDifference = (meetingDate: string) => {
  const dateFromTimestamp = parse(`${meetingDate}`, 'yyyy-MM-dd', new Date())
  const now = new Date()
  return differenceInDays(dateFromTimestamp, now)
}

export const getFileType = (fileName: string) => {
  const extension = fileName?.split('.')?.pop()?.toLowerCase()
  return toAbsoluteUrl(`/media/icons/file_extensions/${extension}.svg`)
}

export const getFormatDateString = (date: string) => {
  const dateObj = new Date(date)
  const options: any = {weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'}
  return dateObj.toLocaleDateString(brandingConfig.defaultLocale, options)
}

export function convertTo12HourFormat(time: string) {
  if (time) {
    const [hours, minutes] = time.split(':')
    const hour = parseInt(hours)
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const twelveHour = hour % 12 || 12
    return `${twelveHour}:${minutes} ${ampm}`
  }
}

export function meetingCountDown(meetingDate: string, startTime: string) {
  if (meetingDate && startTime) {
    const meetingDateTime = parse(`${meetingDate} ${startTime}`, 'yyyy-MM-dd HH:mm', new Date())
    const currentTime = new Date()
    const diffInMinutes = differenceInMinutes(meetingDateTime, currentTime) % 60
    const diffInHours = differenceInHours(meetingDateTime, currentTime)
    const days = Math.floor(diffInHours / 24)
    const hours = diffInHours % 24

    // Construct the countdown string
    return `${days > 0 ? (days !== 1 ? `${days} Days ` : `${days} Day`) : ''}${
      hours > 0 ? (hours !== 1 ? `${hours} Hours ` : `${hours} Hour `) : ''
    }${days === 0 && diffInMinutes > 0 ? `${diffInMinutes} Minutes` : ''}`
  }
}

export function shortenFilename(filename: string) {
  // Split the filename and the extension
  const parts = filename.split('.')
  const extension = parts.pop() // Get the file extension
  const namePart = parts.join('.') // Handle if there are multiple dots in the filename

  // Shorten only if the name part is longer than 10 characters
  if (namePart.length > 10) {
    return `${namePart.substring(0, 10)}...${namePart.substring(namePart.length - 6)}.${extension}`
  }

  return filename
}

export function differenceTime(meetingDate: string, startTime: string, endTime: string) {
  const meetingDateTime = parse(`${meetingDate} ${startTime}`, 'yyyy-MM-dd HH:mm', new Date())
  let endDateTime = parse(`${meetingDate} ${endTime}`, 'yyyy-MM-dd HH:mm', new Date())
  if (endDateTime < meetingDateTime) {
    endDateTime = addDays(endDateTime, 1)
  }
  const diffInMinutes = differenceInMinutes(endDateTime, meetingDateTime)
  return diffInMinutes
}

export function isPastMeeting(meetingDate: string, startTime: string): boolean {
  const meetingDateTime = parse(`${meetingDate} ${startTime}`, 'yyyy-MM-dd HH:mm', new Date())
  const currentDateTime = new Date()
  return isBefore(meetingDateTime, currentDateTime)
}
export function reminderBeforeMeetingTime(meetingDate: string, startTime: string) {
  const meetingDateTime = parse(`${meetingDate} ${startTime}`, 'yyyy-MM-dd HH:mm', new Date())
  const currentDateTime = new Date()
  return differenceInMinutes(meetingDateTime, currentDateTime)
}
